import type { CycleScaleData, JournalData } from "@qendo/profile/journal";
import { Cycle, ScaleType, TrackableType } from "@qendo/trackables";
import { type DateArg, isSameDay, min, roundToNearestMinutes, toDate } from "date-fns";
import { useEffect, useId, useMemo, useState } from "react";
import { Button } from "~/components/Button";
import { JournalTimePicker } from "~/components/form/date-picker/JournalTimePicker";
import { CycleSelector } from "~/components/form/journal/scale/CycleSelector";
import { ListingGroup } from "~/components/layout/ListingGroup";
import { ListingItem } from "~/components/layout/ListingItem";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Space } from "~/constants/sizing";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useJournal } from "~/providers/JournalProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { extractPeriods } from "~/util/qendo/cycle";

import { makeNewScale } from "~/util/qendo/scale";

export type PeriodFormProps = {
    readonly journal?: JournalData<TrackableType.journal, ScaleType.cycle>;
    onRequestClose: () => void;
    onDate?: string | DateArg<Date>;
} & Partial<FullPageModalProps>;

export const PeriodForm = ({ journal, onRequestClose, onDate, ...modalProps }: PeriodFormProps) => {
    const { storePeriod } = useJournal();
    const { addToast, saving, setSaving } = useFeedback();
    const { can, cycle, refreshCycle } = useProfile();
    const [scale, setScale] = useState<CycleScaleData>(journal?.scale ?? makeNewScale(ScaleType.cycle));
    const id = useId();

    // biome-ignore lint/correctness/useExhaustiveDependencies(onDate): value to set
    const current = useMemo(() => {
        const now = new Date();
        return roundToNearestMinutes(onDate && !isSameDay(now, onDate) ? min([now, onDate]) : now, {
            nearestTo: 15,
            roundingMethod: "floor",
        });
    }, []);
    const [at, setAt] = useState<DateArg<Date>>(toDate(journal?.recorded_at ?? current));

    const exists = Boolean(journal);
    const can_save = scale?.value !== null;

    // biome-ignore lint/correctness/useExhaustiveDependencies(journal): readonly
    // biome-ignore lint/correctness/useExhaustiveDependencies(journal.scale.value): readonly
    const expected = useMemo(() => {
        if (journal) {
            return journal.scale.value as Cycle;
        }

        const { current } = extractPeriods(cycle, at);

        return current ? Cycle.ended : Cycle.started;
    }, [cycle, at]);

    const doSave = async () => {
        if (saving) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        const identifier = journal ? { id: journal.id } : {};

        const payload = {
            ...identifier,
            recorded_at: at,
            status: scale?.value ?? expected,
        };

        const should_refresh_cycle = cycle.tracked && can("view", "cycle");

        storePeriod(payload)
            .then(() => addToast({ content: `Period ${exists ? "updated" : "added"}` }))
            .then(async () => should_refresh_cycle && (await refreshCycle()))
            .then(onRequestClose)
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(scale?.value): callback
    useEffect(() => {
        if (!exists && cycle.tracked) {
            scale?.value === expected ||
                setScale({
                    type: ScaleType.cycle,
                    value: expected,
                } as CycleScaleData);
        }
    }, [exists, cycle, expected]);

    return (
        <FullPageModal
            {...modalProps}
            cancelable
            title="Log period"
            onRequestClose={onRequestClose}
            headerRight={() => (
                <Button disabled={!can_save} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
            gap={Space.lg}
        >
            <JournalTimePicker value={at} onDateSelected={setAt} disabled={is_saving} />
            <CycleSelector value={scale} onUpdateScale={setScale} disabled={is_saving} zIndex={30} />

            <ListingGroup>
                <ListingItem
                    title="Record symptoms"
                    href={{ pathname: "/journal/add", params: { search: TrackableType.symptom } }}
                    replace
                />
            </ListingGroup>
        </FullPageModal>
    );
};
